export interface ClientDeviceInfo {
  browser: string
  browser_version: string
  os: string
  os_version: string
  user_agent: string
  platform: string
  language: string
  languages: string
  timezone: string
  screen_width: number
  screen_height: number
  viewport_width: number
  viewport_height: number
  device_type: string
  hardware_concurrency?: number
  device_memory?: number
  cookie_enabled: boolean
  online: boolean
}

function parseUserAgent(ua: string): {
  browser: string
  browserVersion: string
  os: string
  osVersion: string
} {
  let browser = 'unknown'
  let browserVersion = ''
  let os = 'unknown'
  let osVersion = ''

  const matchBrowser = (regex: RegExp, name: string): boolean => {
    const m = ua.match(regex)
    if (m) {
      browser = name
      browserVersion = m[1] || m[2] || ''
      return true
    }
    return false
  }

  if (ua.includes('Edg/')) matchBrowser(/Edg\/([\d.]+)/, 'Edge')
  else if (ua.includes('Chrome') && !ua.includes('Chromium'))
    matchBrowser(/Chrome\/([\d.]+)/, 'Chrome')
  else if (ua.includes('Chromium'))
    matchBrowser(/Chromium\/([\d.]+)/, 'Chromium')
  else if (ua.includes('Firefox')) matchBrowser(/Firefox\/([\d.]+)/, 'Firefox')
  else if (ua.includes('Safari') && !ua.includes('Chrome'))
    matchBrowser(/Version\/([\d.]+).*Safari/, 'Safari')
  else if (ua.includes('OPR/') || ua.includes('Opera'))
    matchBrowser(/(?:OPR|Opera)\/([\d.]+)/, 'Opera')

  if (ua.includes('Windows NT 10')) {
    os = 'Windows'
    osVersion = ua.match(/Windows NT ([\d.]+)/)?.[1] ?? '10'
  } else if (ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Mac OS X')) {
    os = 'macOS'
    const m = ua.match(/Mac OS X ([\d_]+)/)
    if (m) osVersion = m[1].replace(/_/g, '.')
  } else if (ua.includes('iPhone')) {
    os = 'iOS'
    const m = ua.match(/OS ([\d_]+)/)
    if (m) osVersion = m[1].replace(/_/g, '.')
  } else if (ua.includes('iPad')) {
    os = 'iOS'
    const m = ua.match(/OS ([\d_]+)/)
    if (m) osVersion = m[1].replace(/_/g, '.')
  } else if (ua.includes('Android')) {
    os = 'Android'
    const m = ua.match(/Android ([\d.]+)/)
    if (m) osVersion = m[1]
  } else if (ua.includes('Linux')) os = 'Linux'

  return { browser, browserVersion, os, osVersion }
}

let cached: ClientDeviceInfo | null = null

export function getClientDeviceInfo(): ClientDeviceInfo | undefined {
  if (typeof navigator === 'undefined' || typeof window === 'undefined')
    return undefined

  if (cached) return cached

  const ua = navigator.userAgent
  const parsed = parseUserAgent(ua)

  let deviceType = 'desktop'
  if (
    /Mobi|Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      ua
    )
  ) {
    deviceType =
      ua.includes('iPad') ||
      (ua.includes('Mac') && navigator.maxTouchPoints > 1)
        ? 'tablet'
        : 'mobile'
  }

  const screenWidth =
    typeof window.screen?.width === 'number' ? window.screen.width : 0
  const screenHeight =
    typeof window.screen?.height === 'number' ? window.screen.height : 0
  const viewportWidth =
    typeof window.innerWidth === 'number' ? window.innerWidth : 0
  const viewportHeight =
    typeof window.innerHeight === 'number' ? window.innerHeight : 0

  const nav = navigator as Navigator & {
    deviceMemory?: number
    hardwareConcurrency?: number
  }
  const deviceMemory =
    typeof nav.deviceMemory === 'number' ? nav.deviceMemory : undefined
  const hardwareConcurrency =
    typeof nav.hardwareConcurrency === 'number'
      ? nav.hardwareConcurrency
      : undefined

  let timezone = 'unknown'
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    timezone = 'unknown'
  }

  cached = {
    browser: parsed.browser,
    browser_version: parsed.browserVersion,
    os: parsed.os,
    os_version: parsed.osVersion,
    user_agent: ua,
    platform: navigator.platform ?? 'unknown',
    language: navigator.language ?? 'unknown',
    languages: Array.isArray(navigator.languages)
      ? navigator.languages.slice(0, 5).join(',')
      : (navigator.language ?? ''),
    timezone,
    screen_width: screenWidth,
    screen_height: screenHeight,
    viewport_width: viewportWidth,
    viewport_height: viewportHeight,
    device_type: deviceType,
    ...(hardwareConcurrency !== undefined && {
      hardware_concurrency: hardwareConcurrency,
    }),
    ...(deviceMemory !== undefined && { device_memory: deviceMemory }),
    cookie_enabled: navigator.cookieEnabled,
    online: navigator.onLine,
  }

  return cached
}
